/** The placement rules of the page guide bubble. `components/PageGuideSpotlight.vue` uses them. */

export interface GuideRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface BubblePlacement {
  /** Set when the bubble goes below the element, or on the screen without an element. */
  top?: number;
  /** Set when the bubble goes above the element. The bubble height then does not matter. */
  bottom?: number;
  left: number;
}

export const BUBBLE_WIDTH = 300;
const GAP = 12;
const MARGIN = 16;
// The space below the element that the bubble needs. A smaller space puts the bubble above it.
const ROOM_BELOW = 180;

/** The place of the bubble next to the element, or at the lower middle of the screen without one. */
export function bubblePlacement(rect: GuideRect | null, viewport: { width: number; height: number }): BubblePlacement {
  const maxLeft = Math.max(MARGIN, viewport.width - BUBBLE_WIDTH - MARGIN);
  if (rect === null) {
    return { bottom: 32, left: Math.min(maxLeft, Math.max(MARGIN, (viewport.width - BUBBLE_WIDTH) / 2)) };
  }
  const left = Math.min(maxLeft, Math.max(MARGIN, rect.left));
  const bottomEdge = rect.top + rect.height;
  if (viewport.height - bottomEdge >= ROOM_BELOW) return { top: bottomEdge + GAP, left };
  return { bottom: Math.max(MARGIN, viewport.height - rect.top + GAP), left };
}

const isBelow = (path: string, parent: string) => path.startsWith(`${parent.replace(/\/$/, '')}/`);

/**
 * True if the router must open the path of a step. A step without a query keeps the query of the
 * page, for example the page number of a list.
 *
 * A guide has no path with a record id, so a step on the form of a record has the path of its
 * list. The guide never takes the user from a record back to its list: a step stays on a page
 * below its path. If the user opened a page below the page of the previous step, for example a
 * record from the list, a step below that page stays too.
 */
export function needsNavigation(
  stepPath: string,
  current: { path: string; search: string },
  previousStepPath: string | null = null,
): boolean {
  const url = new URL(stepPath, 'https://app.invalid');
  if (url.pathname === current.path) return url.search !== '' && url.search !== current.search;
  if (isBelow(current.path, url.pathname)) return false;
  if (previousStepPath === null) return true;
  const previous = new URL(previousStepPath, 'https://app.invalid').pathname;
  return !(isBelow(current.path, previous) && isBelow(url.pathname, previous));
}
